import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  Package, Calendar, ExternalLink, FolderOpen, Image, 
  CheckCircle, Clock, Loader2, ChevronDown, ChevronRight, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', color: 'bg-gray-500', textColor: 'text-[#AEAEB2]' },
  in_progress: { label: 'En Progreso', color: 'bg-blue-500', textColor: 'text-blue-400' },
  ready: { label: 'Listo', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  delivered: { label: 'Entregado', color: 'bg-green-500', textColor: 'text-green-400' },
  approved: { label: 'Aprobado', color: 'bg-emerald-500', textColor: 'text-emerald-400' }
};

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Get week number of the month (1-5)
function getWeekOfMonth(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const dayOfMonth = date.getDate();
  const firstDayOfWeek = firstDay.getDay() || 7; // Monday = 1
  return Math.ceil((dayOfMonth + firstDayOfWeek - 1) / 7);
}

// Format date range for display
function formatDateRange(startStr, endStr) {
  const start = new Date(startStr + 'T00:00:00');
  const end = new Date(endStr + 'T00:00:00');
  return `${start.getDate()} - ${end.getDate()} ${MONTHS_ES[end.getMonth()].substring(0, 3).toLowerCase()}`;
}

// Deliverable Card Component
function DeliverableCard({ deliverable }) {
  const status = STATUS_CONFIG[deliverable.status] || STATUS_CONFIG.pending;
  const hasFiles = deliverable.drive_folder_url || (deliverable.drive_file_urls && deliverable.drive_file_urls.length > 0);
  const hasPreviews = deliverable.preview_images && deliverable.preview_images.length > 0;
  
  return (
    <div className="bg-gray-700/30 rounded-lg p-3 border border-[rgba(0,0,0,0.08)]/30">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h5 className="text-[#1D1D1F] font-medium text-sm truncate">{deliverable.title}</h5>
          {deliverable.description && (
            <p className="text-[#AEAEB2] text-xs mt-0.5 line-clamp-1">{deliverable.description}</p>
          )}
        </div>
        <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${status.color} text-[#1D1D1F]`}>
          {status.label}
        </span>
      </div>
      
      {/* Preview Images - Compact */}
      {hasPreviews && (
        <div className="flex gap-1.5 mb-2 overflow-x-auto pb-1">
          {deliverable.preview_images.slice(0, 3).map((img, idx) => (
            <a 
              key={idx}
              href={img}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 w-14 h-14 rounded overflow-hidden bg-white/70 hover:opacity-80 transition-opacity"
            >
              <img 
                src={img} 
                alt={`Preview ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </a>
          ))}
          {deliverable.preview_images.length > 3 && (
            <div className="flex-shrink-0 w-14 h-14 rounded bg-white/70 flex items-center justify-center text-[#AEAEB2] text-xs">
              +{deliverable.preview_images.length - 3}
            </div>
          )}
        </div>
      )}
      
      {/* Drive Links */}
      {hasFiles && (
        <div className="flex flex-wrap gap-1.5">
          {deliverable.drive_folder_url && (
            <a
              href={deliverable.drive_folder_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-600/20 text-green-400 rounded text-xs font-medium hover:bg-green-600/30 transition-colors"
            >
              <FolderOpen className="w-3.5 h-3.5" />
              Ver Carpeta
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {deliverable.drive_file_urls && deliverable.drive_file_urls.length > 0 && (
            <a
              href={deliverable.drive_file_urls[0]}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600/20 text-blue-400 rounded text-xs font-medium hover:bg-blue-600/30 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
              Archivos ({deliverable.drive_file_urls.length})
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// Week Row Component
function WeekRow({ weekNum, deliverables, dateRange }) {
  const [expanded, setExpanded] = useState(false);
  const hasDeliverables = deliverables && deliverables.length > 0;
  
  return (
    <div className={`border-l-2 ${hasDeliverables ? 'border-orange-500/50' : 'border-[rgba(0,0,0,0.06)]'} ml-4`}>
      <button
        onClick={() => hasDeliverables && setExpanded(!expanded)}
        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-white/70/30 transition-colors ${!hasDeliverables ? 'opacity-50 cursor-default' : 'cursor-pointer'}`}
        disabled={!hasDeliverables}
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
          hasDeliverables ? 'bg-orange-500/20 text-orange-400' : 'bg-white/70 text-[#AEAEB2]'
        }`}>
          {weekNum}
        </div>
        <div className="flex-1 text-left">
          <p className={`font-medium ${hasDeliverables ? 'text-[#1D1D1F]' : 'text-[#AEAEB2]'}`}>
            Semana {weekNum}
          </p>
          <p className="text-xs text-[#AEAEB2]">{dateRange}</p>
        </div>
        {hasDeliverables && (
          <>
            <span className="text-xs text-orange-400 bg-orange-500/20 px-2 py-1 rounded-full">
              {deliverables.length} entregable{deliverables.length !== 1 ? 's' : ''}
            </span>
            {expanded ? (
              <ChevronDown className="w-5 h-5 text-[#AEAEB2]" />
            ) : (
              <ChevronRight className="w-5 h-5 text-[#AEAEB2]" />
            )}
          </>
        )}
        {!hasDeliverables && (
          <span className="text-xs text-[#6E6E73]">Sin entregables</span>
        )}
      </button>
      
      {/* Expanded Content */}
      {expanded && hasDeliverables && (
        <div className="px-4 pb-4 space-y-2 ml-8">
          {deliverables.map((d, idx) => (
            <DeliverableCard key={d.deliverable_id || idx} deliverable={d} />
          ))}
        </div>
      )}
    </div>
  );
}

// Month Section Component
function MonthSection({ month, year, weeks, isCurrentMonth }) {
  const [expanded, setExpanded] = useState(isCurrentMonth);
  const totalDeliverables = Object.values(weeks).flat().length;
  
  return (
    <div className={`rounded-xl overflow-hidden border ${
      isCurrentMonth 
        ? 'border-orange-500/30 bg-gradient-to-r from-orange-500/5 to-pink-500/5' 
        : 'border-[rgba(0,0,0,0.06)] bg-white/70/20'
    }`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 p-4 hover:bg-white/70/30 transition-colors"
      >
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          isCurrentMonth 
            ? 'bg-gradient-to-br from-orange-500 to-pink-500 text-white' 
            : 'bg-gray-700 text-[#6E6E73]'
        }`}>
          <Calendar className="w-6 h-6" />
        </div>
        <div className="flex-1 text-left">
          <h3 className={`text-lg font-bold ${isCurrentMonth ? 'text-orange-400' : 'text-[#1D1D1F]'}`}>
            {MONTHS_ES[month]}
            {isCurrentMonth && (
              <span className="ml-2 text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full font-normal">
                Mes Actual
              </span>
            )}
          </h3>
          <p className="text-sm text-[#AEAEB2]">{year}</p>
        </div>
        <div className="text-right mr-2">
          {totalDeliverables > 0 ? (
            <span className={`text-sm font-medium ${isCurrentMonth ? 'text-orange-400' : 'text-[#6E6E73]'}`}>
              {totalDeliverables} entregable{totalDeliverables !== 1 ? 's' : ''}
            </span>
          ) : (
            <span className="text-sm text-[#AEAEB2]">Sin entregables</span>
          )}
        </div>
        {expanded ? (
          <ChevronDown className="w-6 h-6 text-[#AEAEB2]" />
        ) : (
          <ChevronRight className="w-6 h-6 text-[#AEAEB2]" />
        )}
      </button>
      
      {/* Expanded Weeks */}
      {expanded && (
        <div className="border-t border-[rgba(0,0,0,0.06)]/50 pb-2">
          {[1, 2, 3, 4, 5].map(weekNum => {
            const weekDeliverables = weeks[weekNum] || [];
            // Calculate approximate date range for this week
            const firstDay = new Date(year, month, (weekNum - 1) * 7 + 1);
            const lastDay = new Date(year, month, Math.min(weekNum * 7, new Date(year, month + 1, 0).getDate()));
            const dateRange = `${firstDay.getDate()} - ${lastDay.getDate()} ${MONTHS_ES[month].substring(0, 3).toLowerCase()}`;
            
            return (
              <WeekRow 
                key={weekNum}
                weekNum={weekNum}
                deliverables={weekDeliverables}
                dateRange={dateRange}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// Main Component
export default function DeliverablesTab() {
  const [deliverables, setDeliverables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDeliverables();
  }, []);

  const loadDeliverables = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(
        `${API_URL}/api/client-portal/my-deliverables-calendar`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeliverables(response.data.months || []);
    } catch (err) {
      console.error('Error loading deliverables:', err);
      // Fallback to old endpoint
      try {
        const token = localStorage.getItem('portal_token');
        const response = await axios.get(
          `${API_URL}/api/client-portal/my-deliverables`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Convert old format to new
        const allDeliverables = [
          ...(response.data.last_week?.deliverables || []),
          ...(response.data.current_week?.deliverables || []),
          ...(response.data.next_week?.deliverables || [])
        ];
        // Group by month and week
        const grouped = groupDeliverablesByMonthAndWeek(allDeliverables);
        setDeliverables(grouped);
      } catch (fallbackErr) {
        setError('No pudimos cargar tus entregables');
      }
    } finally {
      setLoading(false);
    }
  };

  // Group deliverables by month and week
  const groupDeliverablesByMonthAndWeek = (deliverablesList) => {
    const grouped = {};
    const now = new Date();
    
    deliverablesList.forEach(d => {
      const date = new Date(d.week_start + 'T00:00:00');
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      const weekNum = getWeekOfMonth(d.week_start);
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          year: date.getFullYear(),
          month: date.getMonth(),
          weeks: {}
        };
      }
      
      if (!grouped[monthKey].weeks[weekNum]) {
        grouped[monthKey].weeks[weekNum] = [];
      }
      
      grouped[monthKey].weeks[weekNum].push(d);
    });
    
    // Convert to array and sort
    return Object.values(grouped).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Package className="w-12 h-12 text-[#6E6E73] mx-auto mb-3" />
        <p className="text-[#AEAEB2]">{error}</p>
      </div>
    );
  }

  // Generate months view (current + past 6 months)
  const now = new Date();
  const monthsToShow = [];
  
  for (let i = 0; i < 6; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthData = deliverables.find(
      m => m.year === date.getFullYear() && m.month === date.getMonth()
    );
    
    monthsToShow.push({
      year: date.getFullYear(),
      month: date.getMonth(),
      weeks: monthData?.weeks || {},
      isCurrent: i === 0
    });
  }

  const hasAnyDeliverables = deliverables.length > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl">
          <Package className="w-6 h-6 text-[#1D1D1F]" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-[#1D1D1F]">Tus Entregables</h2>
          <p className="text-sm text-[#AEAEB2]">Contenido organizado por mes y semana</p>
        </div>
      </div>

      {/* Calendar View */}
      <div className="space-y-3">
        {monthsToShow.map((monthData, idx) => (
          <MonthSection
            key={`${monthData.year}-${monthData.month}`}
            year={monthData.year}
            month={monthData.month}
            weeks={monthData.weeks}
            isCurrentMonth={monthData.isCurrent}
          />
        ))}
      </div>

      {/* Empty state if no deliverables at all */}
      {!hasAnyDeliverables && (
        <div className="text-center py-8 bg-white/70/30 rounded-xl border border-[rgba(0,0,0,0.06)]">
          <Package className="w-12 h-12 text-[#6E6E73] mx-auto mb-3" />
          <p className="text-[#AEAEB2]">Aún no hay entregables registrados</p>
          <p className="text-sm text-[#AEAEB2] mt-1">
            Aquí verás el contenido que tu equipo prepara cada semana
          </p>
        </div>
      )}
    </div>
  );
}
